import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BancoldexService } from 'src/app/comun/services/bancoldex.service';
import { HOME_CONTENT_MOCK } from '../data/home-content.mock';
import type {
  HomeParaMiBannersActivosResponse,
  HomeParaMiContent,
} from '../models/home-para-mi-content.model';
import { mapQueEsBannersToContent } from '../utils/home-para-mi-banner.mapper';
import { prepareParaMiTitleHtml } from '../utils/home-para-mi-html.util';

/** Debe coincidir con `NeoBannerDTO.QUE_ES_NEOCREDITO` */
export const QUE_ES_BANNER_TIPO = 'QueEsNeocredito';

@Injectable({
  providedIn: 'root',
})
export class HomeParaMiService {
  private readonly fallbackContent: HomeParaMiContent = buildParaMiFallbackFromMock();

  constructor(private readonly bancoldexService: BancoldexService) {}

  loadContent(): Observable<HomeParaMiContent> {
    return this.bancoldexService.getArchivosBannerActivos(QUE_ES_BANNER_TIPO).pipe(
      map((response: HomeParaMiBannersActivosResponse) => {
        const rows = normalizeQueEsBannerRows(response);
        if (!rows.length) {
          return this.fallbackContent;
        }
        return mapQueEsBannersToContent(rows, this.fallbackContent);
      }),
      catchError(() => of(this.fallbackContent))
    );
  }
}

function buildParaMiFallbackFromMock(): HomeParaMiContent {
  const mock = HOME_CONTENT_MOCK.paraMi;
  return {
    sectionTitle: prepareParaMiTitleHtml(mock.sectionTitle),
    natural: {
      title: mock.natural.title,
      description: mock.natural.description,
      buttonLabel: mock.natural.buttonLabel,
      buttonUrl: mock.natural.buttonUrl,
      pasos: mock.natural.pasos.map((descripcion) => ({ descripcion })),
    },
    juridica: {
      title: mock.juridica.title,
      description: mock.juridica.description,
      buttonLabel: mock.juridica.buttonLabel,
      buttonUrl: mock.juridica.buttonUrl,
      pasos: mock.juridica.pasos.map((descripcion) => ({ descripcion })),
    },
  };
}

function normalizeQueEsBannerRows(
  response: HomeParaMiBannersActivosResponse
): HomeParaMiBannersActivosResponse['banners'] {
  if (!response?.exitoso || !response.banners?.length) {
    return [];
  }

  const first = response.banners[0] as unknown;
  if (Array.isArray(first)) {
    return (response.banners as unknown as HomeParaMiBannersActivosResponse['banners'][]).flat();
  }

  return response.banners;
}
